<template>
    <Head title="Sponsor" />
    <div class="min-h-screen bg-[#F6F8FA] font-sans text-slate-900">
        <header class="border-b border-slate-200 bg-white">
            <div class="mx-auto flex h-14 max-w-5xl items-center justify-between px-4 sm:px-6">
                <a href="/" class="flex items-center gap-2.5 text-sm text-slate-500">
                    <img src="@/assets/logo-full.svg" alt="Heesify" class="h-8 w-auto brightness-0" />
                    <span class="border-l border-slate-200 pl-2.5">Sponsors</span>
                </a>
                <button
                    v-if="ads.length"
                    type="button"
                    class="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                    @click="creating ? closeForm() : openNew()"
                >
                    {{ creating ? "Back to my ads" : "New ad" }}
                </button>
            </div>
        </header>

        <main class="mx-auto max-w-5xl px-4 py-8 sm:px-6">
            <form v-if="creating" novalidate @submit.prevent="submit">
                <h1 class="text-2xl font-semibold tracking-tight">{{ editing ? "Edit your ad" : "Create your audio ad" }}</h1>
                <p class="mt-1 text-sm text-slate-500">{{ editing ? "You can change it until we approve it. After that it's locked." : "Reach Somali listeners with a short message between songs in the Heesify app." }}</p>

                <div class="mt-6 grid items-start gap-6 lg:grid-cols-[1fr_20rem]">
                    <div class="space-y-6">
                        <section v-if="!editing" class="rounded-xl border border-slate-200 bg-white p-5 sm:p-6">
                            <h2 class="font-semibold">Plan</h2>
                            <div class="mt-3 grid gap-3 sm:grid-cols-2" role="radiogroup" aria-label="Plan">
                                <button
                                    v-for="option in plans"
                                    :key="option.key"
                                    type="button"
                                    role="radio"
                                    :aria-checked="form.plan === option.key"
                                    class="rounded-lg border p-4 text-left transition"
                                    :class="form.plan === option.key ? 'border-slate-900 ring-1 ring-slate-900' : 'border-slate-300 hover:border-slate-400'"
                                    @click="form.plan = option.key"
                                >
                                    <span class="flex items-center justify-between gap-2">
                                        <span class="text-sm font-medium">{{ option.label }}</span>
                                        <span class="text-lg font-semibold">${{ option.price }}</span>
                                    </span>
                                    <span class="mt-1 block text-xs text-slate-500">{{ option.hint }}</span>
                                </button>
                            </div>
                            </section>

                        <p v-if="errors.plan" class="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{{ errors.plan }}</p>

                        <section class="space-y-4 rounded-xl border border-slate-200 bg-white p-5 sm:p-6">
                            <h2 class="font-semibold">Your ad</h2>
                            <div class="grid gap-4 sm:grid-cols-2">
                                <label class="block">
                                    <span class="text-sm font-medium text-slate-700">Business name</span>
                                    <input v-model="form.advertiser" maxlength="80" autocomplete="organization" :class="input(errors.advertiser)" />
                                    <span v-if="errors.advertiser" class="mt-1.5 block text-sm text-red-600">{{ errors.advertiser }}</span>
                                </label>
                                <label class="block">
                                    <span class="text-sm font-medium text-slate-700">Ad title</span>
                                    <input v-model="form.title" maxlength="80" :class="input(errors.title)" />
                                    <span v-if="errors.title" class="mt-1.5 block text-sm text-red-600">{{ errors.title }}</span>
                                </label>
                            </div>

                            <FileDrop
                                label="Audio"
                                :hint="editing ? `Keeping your current audio. Drop a new one to replace it (up to ${limits.seconds} seconds)` : `MP3 or MP4, up to ${limits.seconds} seconds and ${limits.audio_mb} MB`"
                                :detail="audioDetail"
                                accept=".mp3,.mp4,audio/mpeg,video/mp4"
                                :file="form.audio"
                                :error="errors.audio"
                                @pick="pickAudio"
                            />
                            <audio v-if="audioUrl" :src="audioUrl" controls class="h-9 w-full"></audio>

                            <FileDrop
                                label="Picture"
                                :hint="editing ? `Keeping your current picture. Drop a new one to replace it` : `Shown while it plays. JPG, PNG or WebP, up to ${limits.image_mb} MB`"
                                accept="image/jpeg,image/png,image/webp"
                                :file="form.image"
                                :error="errors.image"
                                @pick="pickImage"
                            >
                                <template #preview>
                                    <img v-if="imageUrl" :src="imageUrl" alt="" class="h-16 w-16 shrink-0 rounded-md object-cover" />
                                </template>
                            </FileDrop>

                            <label class="block">
                                <span class="text-sm font-medium text-slate-700">Link</span>
                                <input v-model="form.click_url" type="url" inputmode="url" placeholder="https://yourbusiness.com" :class="input(errors.click_url)" />
                                <span v-if="errors.click_url" class="mt-1.5 block text-sm text-red-600">{{ errors.click_url }}</span>
                                <span v-else class="mt-1.5 block text-xs text-slate-500">Where people go when they tap Learn more. We add UTM tags so you can see visits in your analytics.</span>
                            </label>
                        </section>

                        <section class="rounded-xl border border-slate-200 bg-white p-5 sm:p-6">
                            <h2 class="font-semibold">Audience</h2>
                            <div class="mt-3 inline-flex rounded-lg bg-slate-100 p-1 text-sm font-medium">
                                <button type="button" class="rounded-md px-4 py-1.5" :class="!targeted ? 'bg-white shadow-sm' : 'text-slate-600'" @click="setTargeted(false)">Everyone</button>
                                <button type="button" class="rounded-md px-4 py-1.5" :class="targeted ? 'bg-white shadow-sm' : 'text-slate-600'" @click="setTargeted(true)">Choose</button>
                            </div>

                            <div v-if="targeted" class="mt-5 space-y-5">
                                <div>
                                    <p class="text-sm font-medium text-slate-700">Gender</p>
                                    <div class="mt-2 flex flex-wrap gap-2">
                                        <button v-for="gender in genderOptions" :key="gender.value" type="button" :class="chip(form.genders.includes(gender.value))" :aria-pressed="form.genders.includes(gender.value)" @click="toggle('genders', gender.value)">{{ gender.label }}</button>
                                    </div>
                                </div>
                                <div>
                                    <p class="text-sm font-medium text-slate-700">Age</p>
                                    <div class="mt-2 flex flex-wrap gap-2">
                                        <button v-for="age in ageGroups" :key="age" type="button" :class="chip(form.ages.includes(age))" :aria-pressed="form.ages.includes(age)" @click="toggle('ages', age)">{{ age }}</button>
                                    </div>
                                </div>
                                <div class="grid gap-5 sm:grid-cols-2">
                                    <MultiPick v-model="form.countries" label="Countries" placeholder="Search countries" :options="countryOptions" />
                                    <MultiPick v-model="form.genres" label="Genre" placeholder="Search genres" :options="genreOptions" />
                                </div>
                                <p class="text-xs text-slate-500">Anything you leave empty means everyone.</p>
                            </div>
                        </section>
                    </div>

                    <aside class="rounded-xl border border-slate-200 bg-white p-5 lg:sticky lg:top-6">
                        <h2 class="font-semibold">Summary</h2>
                        <div class="mt-3 flex items-center gap-3 rounded-lg bg-slate-50 p-3">
                            <img v-if="imageUrl" :src="imageUrl" alt="" class="h-12 w-12 shrink-0 rounded-md object-cover" />
                            <div v-else class="h-12 w-12 shrink-0 rounded-md bg-slate-200"></div>
                            <div class="min-w-0">
                                <p class="truncate text-sm font-medium">{{ form.title || "Your ad title" }}</p>
                                <p class="truncate text-xs text-slate-500">Sponsored · {{ form.advertiser || "Your business" }}</p>
                            </div>
                        </div>
                        <dl class="mt-4 space-y-2 text-sm">
                            <div class="flex justify-between gap-3"><dt class="text-slate-500">Plan</dt><dd class="text-right">{{ editing ? editing.plan : currentPlan?.label }}</dd></div>
                            <div class="flex justify-between gap-3"><dt class="text-slate-500">Audience</dt><dd class="text-right">{{ audienceSummary }}</dd></div>
                            <div v-if="!editing" class="flex justify-between gap-3 border-t border-slate-200 pt-3 font-semibold"><dt>Total</dt><dd>${{ currentPlan?.price }} USD</dd></div>
                        </dl>
                        <button type="submit" :disabled="busy" class="mt-4 w-full rounded-lg bg-slate-900 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-60">
                            {{ busyLabel }}
                        </button>
                        <p v-if="!editing" class="mt-3 text-center text-xs text-slate-500">Secure payment by Stripe. We listen to every ad before it plays, and refund you if we can't run it.</p>
                    </aside>
                </div>
            </form>

            <template v-else>
                <h1 class="text-2xl font-semibold tracking-tight">Your ads</h1>
                <div class="mt-6 space-y-4">
                    <article v-for="ad in ads" :key="ad.id" class="rounded-xl border border-slate-200 bg-white p-5">
                        <div class="flex items-start gap-4">
                            <img :src="ad.image_url" alt="" class="h-14 w-14 shrink-0 rounded-md object-cover" />
                            <div class="min-w-0 flex-1">
                                <div class="flex flex-wrap items-center gap-2">
                                    <h2 class="truncate font-semibold">{{ ad.title }}</h2>
                                    <span class="rounded-md px-2 py-0.5 text-xs font-medium" :class="statusStyle(ad.status)">{{ statusLabel(ad.status) }}</span>
                                </div>
                                <p class="mt-0.5 text-sm text-slate-500">{{ ad.plan }} · ${{ ad.price }}<template v-if="ad.ends_at"> · until {{ day(ad.ends_at) }}</template></p>
                                <a :href="ad.click_url" target="_blank" rel="noopener noreferrer" class="mt-0.5 block truncate text-sm text-slate-700 underline">{{ ad.click_url }}</a>
                            </div>
                            <div class="flex shrink-0 flex-wrap justify-end gap-2">
                                <button v-if="ad.editable" type="button" class="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50" :disabled="busy" @click="openEdit(ad)">Edit</button>
                                <button v-if="ad.status === 'awaiting_payment'" type="button" class="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white" :disabled="busy" @click="pay(ad)">Finish payment</button>
                            </div>
                        </div>

                        <p v-if="ad.review_note" class="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{{ ad.review_note }}</p>

                        <dl class="mt-4 grid grid-cols-3 gap-3 border-t border-slate-100 pt-4">
                            <div>
                                <dt class="text-xs text-slate-500">Plays</dt>
                                <dd class="mt-0.5 text-lg font-semibold">{{ ad.impressions.toLocaleString() }}<span v-if="ad.goal" class="text-sm font-normal text-slate-500"> / {{ ad.goal.toLocaleString() }}</span></dd>
                            </div>
                            <div>
                                <dt class="text-xs text-slate-500">Learn more clicks</dt>
                                <dd class="mt-0.5 text-lg font-semibold">{{ ad.clicks.toLocaleString() }}</dd>
                            </div>
                            <div>
                                <dt class="text-xs text-slate-500">Click rate</dt>
                                <dd class="mt-0.5 text-lg font-semibold">{{ ad.click_rate }}%</dd>
                            </div>
                        </dl>
                        <div v-if="ad.goal" class="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-100">
                            <div class="h-full rounded-full bg-slate-900" :style="{ width: `${Math.min(100, (ad.impressions / ad.goal) * 100)}%` }"></div>
                        </div>
                    </article>
                </div>
            </template>
        </main>
    </div>
</template>

<script>
import axios from "axios";
import { Head } from "@inertiajs/vue3";
import FileDrop from "@/components/sponsor/FileDrop.vue";
import MultiPick from "@/components/sponsor/MultiPick.vue";

const STATUS = {
    awaiting_payment: ["Not paid yet", "bg-slate-100 text-slate-700"],
    in_review: ["In review", "bg-amber-100 text-amber-800"],
    running: ["Running", "bg-emerald-100 text-emerald-800"],
    finished: ["Finished", "bg-slate-100 text-slate-700"],
    rejected: ["Refunded", "bg-red-100 text-red-700"],
    stopped: ["Stopped", "bg-slate-100 text-slate-700"],
};

const blankForm = (plan = "plays") => ({ plan, advertiser: "", title: "", audio: null, image: null, click_url: "", countries: [], genders: [], ages: [], genres: [] });

export default {
    // A clean page of its own, without the music site's sidebar and player
    layout: (_, page) => page,
    components: { Head, FileDrop, MultiPick },
    props: {
        ads: { type: Array, default: () => [] },
        plans: { type: Array, default: () => [] },
        limits: { type: Object, default: () => ({}) },
        countries: { type: Array, default: () => [] },
        genres: { type: Array, default: () => [] },
        ageGroups: { type: Array, default: () => [] },
    },
    data() {
        return {
            creating: this.ads.length === 0,
            editing: null,
            targeted: false,
            busy: false,
            progress: 0,
            errors: {},
            audioUrl: null,
            imageUrl: null,
            audioSeconds: null,
            genderOptions: [
                { value: "female", label: "Women" },
                { value: "male", label: "Men" },
            ],
            form: blankForm(this.plans[0]?.key),
        };
    },
    computed: {
        currentPlan() {
            return this.plans.find((plan) => plan.key === this.form.plan);
        },
        countryOptions() {
            return this.countries.map((country) => ({ value: country.code, label: country.name }));
        },
        genreOptions() {
            return this.genres.map((genre) => ({ value: genre.id, label: genre.name }));
        },
        audioDetail() {
            return this.audioSeconds ? `${Math.round(this.audioSeconds)} seconds` : "";
        },
        audienceSummary() {
            const { countries, genders, ages, genres } = this.form;
            return countries.length || genders.length || ages.length || genres.length ? "Chosen audience" : "Everyone";
        },
        busyLabel() {
            if (!this.busy) return this.editing ? "Save changes" : "Continue to payment";
            if (this.progress && this.progress < 100) return `Uploading ${this.progress}%`;
            return this.editing ? "Saving..." : "Opening payment...";
        },
    },
    beforeUnmount() {
        [this.audioUrl, this.imageUrl].forEach((url) => url && URL.revokeObjectURL(url));
    },
    methods: {
        resetForm(form = blankForm(this.plans[0]?.key), urls = {}) {
            [this.audioUrl, this.imageUrl].forEach((url) => url?.startsWith("blob:") && URL.revokeObjectURL(url));
            Object.assign(this, { form, errors: {}, audioSeconds: null, audioUrl: urls.audio ?? null, imageUrl: urls.image ?? null });
        },
        openNew() {
            this.resetForm();
            Object.assign(this, { editing: null, targeted: false, creating: true });
        },
        closeForm() {
            this.resetForm();
            Object.assign(this, { editing: null, targeted: false, creating: false });
        },
        openEdit(ad) {
            this.resetForm(
                { ...blankForm(), advertiser: ad.advertiser, title: ad.title, click_url: ad.click_url, countries: [...ad.countries], genders: [...ad.genders], ages: [...ad.ages], genres: [...ad.genres] },
                { audio: ad.audio_url, image: ad.image_url }
            );
            this.editing = ad;
            this.targeted = ad.countries.length + ad.genders.length + ad.ages.length + ad.genres.length > 0;
            this.creating = true;
            window.scrollTo(0, 0);
        },
        statusLabel: (status) => STATUS[status]?.[0] ?? status,
        statusStyle: (status) => STATUS[status]?.[1] ?? "bg-slate-100 text-slate-700",
        day: (date) => new Date(date).toLocaleDateString(),
        input(error) {
            return [
                "mt-1.5 block w-full rounded-lg border bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-slate-900 focus:ring-1 focus:ring-slate-900",
                error ? "border-red-400" : "border-slate-300",
            ];
        },
        chip(on) {
            return [
                "rounded-lg border px-3 py-1.5 text-sm font-medium transition",
                on ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 bg-white text-slate-700 hover:border-slate-400",
            ];
        },
        toggle(list, value) {
            this.form[list] = this.form[list].includes(value) ? this.form[list].filter((item) => item !== value) : [...this.form[list], value];
        },
        setTargeted(on) {
            this.targeted = on;
            if (!on) Object.assign(this.form, { countries: [], genders: [], ages: [], genres: [] });
        },
        // Saves a wasted upload. The server checks the file again itself.
        pickAudio(file) {
            if (this.audioUrl?.startsWith("blob:")) URL.revokeObjectURL(this.audioUrl);
            this.errors = { ...this.errors, audio: null };
            this.form.audio = file;
            this.audioSeconds = null;
            this.audioUrl = URL.createObjectURL(file);
            if (file.size > this.limits.audio_mb * 1024 * 1024) {
                this.errors.audio = `The audio can be at most ${this.limits.audio_mb} MB.`;
                return;
            }
            const audio = new Audio();
            audio.onloadedmetadata = () => {
                this.audioSeconds = audio.duration;
                if (audio.duration > this.limits.seconds + 0.5) {
                    this.errors = { ...this.errors, audio: `The audio can be at most ${this.limits.seconds} seconds. Yours is ${Math.ceil(audio.duration)} seconds.` };
                }
            };
            audio.src = this.audioUrl;
        },
        pickImage(file) {
            if (this.imageUrl?.startsWith("blob:")) URL.revokeObjectURL(this.imageUrl);
            this.errors = { ...this.errors, image: null };
            this.form.image = file;
            this.imageUrl = URL.createObjectURL(file);
            if (file.size > this.limits.image_mb * 1024 * 1024) {
                this.errors.image = `The picture can be at most ${this.limits.image_mb} MB.`;
            }
        },
        missing() {
            const found = {};
            if (!this.form.advertiser.trim()) found.advertiser = "Enter your business name.";
            if (!this.form.title.trim()) found.title = "Enter a title for your ad.";
            if (!this.form.audio && !this.editing) found.audio = "Add your audio.";
            if (!this.form.image && !this.editing) found.image = "Add a picture.";
            if (!this.form.click_url.trim().startsWith("https://")) found.click_url = "Enter a full link starting with https://";
            return found;
        },
        showErrors(found) {
            this.errors = { ...this.errors, ...found };
            this.$nextTick(() => document.querySelector(".text-red-600")?.scrollIntoView({ behavior: "smooth", block: "center" }));
        },
        async post(url, data, options) {
            // An expired CSRF token here redirects home instead of failing
            await axios.get("/sanctum/csrf-cookie");
            return axios.post(url, data, options);
        },
        async submit() {
            const found = this.missing();
            if (Object.keys(found).length) return this.showErrors(found);
            if (this.errors.audio || this.errors.image) return this.showErrors({});
            this.busy = true;
            this.progress = 0;
            this.errors = {};
            const data = new FormData();
            Object.entries(this.form).forEach(([key, value]) => {
                if (Array.isArray(value)) value.forEach((item) => data.append(`${key}[]`, item));
                else if (value !== null) data.append(key, value);
            });
            try {
                const response = await this.post(this.editing ? `/sponsor/ads/${this.editing.id}` : "/sponsor/ads", data, {
                    onUploadProgress: (event) => {
                        if (event.total) this.progress = Math.round((event.loaded / event.total) * 100);
                    },
                });
                window.location.href = response.data.checkout_url ?? "/sponsor";
            } catch (error) {
                const server = error.response?.data?.errors ?? {};
                this.showErrors(
                    Object.keys(server).length
                        ? Object.fromEntries(Object.entries(server).map(([key, list]) => [key.split(".")[0], list[0]]))
                        : { plan: error.response?.data?.message || "That didn't go through. Please try again." }
                );
                this.busy = false;
            }
        },
        async pay(ad) {
            this.busy = true;
            try {
                const response = await this.post(`/sponsor/ads/${ad.id}/pay`);
                window.location.href = response.data.checkout_url;
            } catch (error) {
                this.busy = false;
            }
        },
    },
};
</script>
